import { useState, useEffect } from 'react';
import { getAnalyticsOverview, getBusinessMetrics } from '../../lib/api';
import { Card } from '../ui/card';
import { Eye, MousePointer, Users, Mail, TrendingUp, DollarSign } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

function StatCard({ icon: Icon, label, value, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-400',
    green: 'bg-emerald-500/10 text-emerald-400',
    purple: 'bg-purple-500/10 text-purple-400',
    orange: 'bg-orange-500/10 text-orange-400',
  };
  return (
    <Card className="p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-zinc-500">{label}</p>
          <p className="text-2xl font-semibold text-zinc-100">{value}</p>
        </div>
      </div>
    </Card>
  );
}

export default function DashboardOverview() {
  const [analytics, setAnalytics] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getAnalyticsOverview(), getBusinessMetrics()])
      .then(([a, m]) => {
        setAnalytics(a.data);
        setMetrics(m.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-900/50 border-t-blue-400 rounded-full animate-spin" />
      </div>
    );
  }

  const fmt = (n) => {
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-100">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Overview of your landing page performance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={analytics?.total_views?.toLocaleString() || 0} color="blue" />
        <StatCard icon={MousePointer} label="CTA Clicks" value={analytics?.total_clicks?.toLocaleString() || 0} color="purple" />
        <StatCard icon={Users} label="Demo Requests" value={analytics?.total_demos?.toLocaleString() || 0} color="green" />
        <StatCard icon={Mail} label="Emails Sent" value={analytics?.emails_sent?.toLocaleString() || 0} color="orange" />
      </div>

      {/* Revenue KPIs */}
      {metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={DollarSign} label="MRR" value={fmt(metrics.mrr)} color="green" />
          <StatCard icon={DollarSign} label="ARR" value={fmt(metrics.arr)} color="blue" />
          <StatCard icon={TrendingUp} label="Pipeline Value" value={fmt(metrics.pipeline_value)} color="purple" />
          <StatCard icon={TrendingUp} label="Conversion Rate" value={`${metrics.conversion_rate}%`} color="orange" />
        </div>
      )}

      {/* Daily Views Chart */}
      {analytics?.daily_views?.length > 0 && (
        <Card className="p-5">
          <h2 className="text-base font-medium text-zinc-100 mb-4">Daily Page Views (Last 30 Days)</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.daily_views}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="views" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}
